import { toUpper } from 'lodash';
import pluralize from 'pluralize';
import {
  type MouseEvent,
  memo,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { type PublicClip } from '@air/api';
import { type BaseCustomField, type Clip, ClipType } from '@air/api/types';
import { EllipsisIcon, LayersIcon, MessageSquareIcon, MousePointerClickIcon } from '@air/design-icons';
import { Spinner } from '@air/primitive-spinner';
import { Tooltip } from '@air/primitive-tooltip';
import { formatBytes } from '@air/utilities';
import { ClipUtils, isPdfRenderedExtension } from '@air/utils-clips';
import { cn } from '@air/utils-tailwind';

import { CardAction } from '~/components/CardAction';
import { CustomFieldsDisplay } from '~/components/Gallery/CustomFieldsDisplay';
import { GalleryCardCustomFieldsAction } from '~/components/Gallery/GalleryCardCustomFieldsAction';
import { GalleryItemType } from '~/components/Gallery/types';
import { getAssetCustomFields } from '~/components/Gallery/utils';
import { VideoScrubbableThumbnail } from '~/components/VideoScrubbableThumbnail';
import { VideoStaticThumbnail } from '~/components/VideoStaticThumbnail';
import { GalleryAssetMenu } from '~/components/v2/components/shared/gallery/GalleryAssetMenu';
import { GalleryAssetShortInfo } from '~/components/v2/components/shared/gallery/GalleryAssetShortInfo';
import { GalleryAssetThumbnail } from '~/components/v2/components/shared/gallery/GalleryAssetThumbnail';
import { GalleryAssetTitle } from '~/components/v2/components/shared/gallery/GalleryAssetTitle';
import {
  GallerySelectableCard,
  type GallerySelectableCardProps,
} from '~/components/v2/components/shared/gallery/GallerySelectableCard';
import { useVideoScrubbableThumbnail } from '~/hooks/useVideoScrubbableThumbnail';
import {
  isTitleAndMetadataVisibleSelector,
  visibleCustomFieldsViewOptionsSelector,
} from '~/store/configViews/selectors';
import { type SelectableGalleryAssetItem } from '~/store/selectedItems/types';
import { getCanvasImportPreview } from '~/utils/canvasImportPreviewCache';
import { useAirSelector } from '~/utils/ReduxUtils';
import { getUploadPreview } from '~/utils/uploadPreviewCache';
import { clipSupportsStaticThumbnail } from '~/utils/videoThumbnails';

export type GalleryAssetProps<C extends Clip | PublicClip> = Pick<
  GallerySelectableCardProps<SelectableGalleryAssetItem>,
  'getItemMenuOptions' | 'getSelectionMenuOptions' | 'isSelectable'
> & {
  allCustomFields: BaseCustomField[] | undefined;
  canViewCustomFields: boolean;
  clip: C;
  height: number;
  onAssetClick: () => void;
  onAssetTitleCmdClick?: () => void;
  onCustomFieldsClick: () => void;
  onDiscussionsClick: () => void;
  onOpenInCanvas?: () => void;
  onVersionsClick: () => void;
  showVersionsIndicator: boolean;
  shouldShowDiscussions: boolean;
  startBadges?: ReactNode;
  width: number;
};

const _GalleryAsset = <C extends Clip | PublicClip>({
  allCustomFields = [],
  canViewCustomFields,
  clip,
  getItemMenuOptions,
  getSelectionMenuOptions,
  height,
  isSelectable,
  onAssetClick,
  onAssetTitleCmdClick,
  onCustomFieldsClick,
  onDiscussionsClick,
  onOpenInCanvas,
  onVersionsClick,
  showVersionsIndicator,
  shouldShowDiscussions,
  startBadges,
  width,
}: GalleryAssetProps<C>) => {
  const [hasCanvasImportPreview, setHasCanvasImportPreview] = useState(
    () => !!getCanvasImportPreview(clip.importedName),
  );
  const [hasUploadPreview, setHasUploadPreview] = useState(() => !!getUploadPreview(clip.importedName));

  const handlePreviewReleased = useCallback(() => {
    setHasCanvasImportPreview(false);
    setHasUploadPreview(false);
  }, []);

  const hasCachedPreview = hasCanvasImportPreview || hasUploadPreview;

  // When the spriteSheet becomes available, the clip card switches to VideoScrubbableThumbnail/
  // VideoStaticThumbnail — neither calls onPreviewReleased, so clear the shimmer here instead.
  useEffect(() => {
    if (clip.assets.spriteSheet?.src) {
      handlePreviewReleased();
    }
  }, [clip.assets.spriteSheet?.src, handlePreviewReleased]);

  const isTitleAndMetadataVisible = useAirSelector(isTitleAndMetadataVisibleSelector);
  const visibleCustomFields = useAirSelector(visibleCustomFieldsViewOptionsSelector);

  const { backgroundImageRef, backgroundImageStyles, indicatorRef, onMouseLeave, onMouseMove } =
    useVideoScrubbableThumbnail({
      spritesheet: clip.assets.spriteSheet,
      containerDimensions: {
        height,
        width,
      },
    });

  const customFields = useMemo(
    () => getAssetCustomFields({ allCustomFields: allCustomFields || [], clip, visibleCustomFields }),
    [allCustomFields, clip, visibleCustomFields],
  );

  const shouldShowCustomFields = canViewCustomFields && customFields.length > 0;

  const clipDimensions = useMemo(() => {
    const aspectRatio = clip.width / clip.height;

    return {
      width,
      height: width / aspectRatio,
    };
  }, [width, clip.height, clip.width]);

  const item = useMemo<SelectableGalleryAssetItem>(
    () => ({
      id: clip.id,
      item: clip,
      type: GalleryItemType.asset,
    }),
    [clip],
  );

  const meta = useMemo(() => {
    const meta = [];

    if (clip.ext) {
      meta.push(toUpper(clip.ext));
    }

    if (clip.size) {
      meta.push(formatBytes(clip.size));
    }

    if (clip.pages) {
      meta.push(pluralize('page', clip.pages, true));
    }

    if (!isPdfRenderedExtension(clip.ext) && clip.width && clip.height) {
      meta.push(`${clip.width} x ${clip.height}`);
    }

    return meta.join(' · ');
  }, [clip.ext, clip.height, clip.pages, clip.size, clip.width]);

  const popoverCustomFields = useMemo(
    () =>
      // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
      clip?.customFields?.filter((customField) => {
        return allCustomFields.some((cf) => cf.id === customField.id);
      }),
    [clip, allCustomFields],
  );

  const onTitleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if ((event.metaKey || event.ctrlKey) && onAssetTitleCmdClick) {
        onAssetTitleCmdClick?.();
      } else {
        // biome-ignore lint/suspicious/noUnnecessaryConditions: PLA-1724 — pre-existing debt, not introduced by the icon migration
        onAssetClick?.();
      }
    },
    [onAssetClick, onAssetTitleCmdClick],
  );

  const handleOpenInCanvas = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onOpenInCanvas?.();
    },
    [onOpenInCanvas],
  );

  return (
    <GallerySelectableCard
      data-testid="ASSET_CARD"
      data-asset-id={clip.id}
      // The card radii read the --gallery-card-* vars (set by the public collection page's Corner
      // rounding setting — see shareLinkThumbnailRadius.ts) and fall back to the native values, so
      // every surface that doesn't set the vars renders exactly as before.
      className="flex size-full flex-col rounded-[var(--gallery-card-radius,16px)] ring-2 ring-transparent ring-inset transition-colors ads:data-[hovering=true]:bg-hover-subtle ads:data-[selected=true]:bg-hover-subtle data-[hovering=true]:bg-gray-4 data-[selected=true]:bg-gray-4 ads:data-[highlighted=true]:ring-focus ads:data-[selected=true]:ring-primary data-[highlighted=true]:ring-blue-9 data-[selected=true]:ring-blue-9"
      getItemMenuOptions={getItemMenuOptions}
      getSelectionMenuOptions={getSelectionMenuOptions}
      isSelectable={isSelectable}
      item={item}
      onClick={onAssetClick}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      data-clip-state={clip.status}
    >
      {({ isHovering, setIsMenuOpen }) => {
        let thumbnail: ReactElement;

        if (clip.assets.spriteSheet?.src && clip.type === ClipType.video && isHovering) {
          thumbnail = (
            <VideoScrubbableThumbnail
              backgroundImageRef={backgroundImageRef}
              indicatorRef={indicatorRef}
              src={clip.assets.spriteSheet.src}
              style={backgroundImageStyles}
            />
          );
        } else if (clipSupportsStaticThumbnail(clip)) {
          thumbnail = (
            <VideoStaticThumbnail
              clip={clip}
              containerDimensions={{
                height,
                width,
              }}
            />
          );
        } else {
          thumbnail = (
            <GalleryAssetThumbnail
              clip={clip}
              isHovering={isHovering}
              width={clipDimensions.width}
              height={clipDimensions.height}
              onPreviewReleased={handlePreviewReleased}
            />
          );
        }

        return (
          <>
            <div className="pointer-events-none relative grow">
              <div
                className={cn(
                  'absolute inset-1 flex overflow-hidden rounded-[var(--gallery-card-inner-radius,12px)]',
                  hasCachedPreview && !ClipUtils.isProcessing(clip.status) && 'ads:bg-ads-gray-4 bg-gray-4',
                )}
              >
                {thumbnail}
              </div>

              {hasCachedPreview && !ClipUtils.isProcessing(clip.status) && !clip.assets.spriteSheet?.src && (
                <div className="shimmer-image-overlay absolute inset-0 rounded-[var(--gallery-card-inner-radius,12px)]" />
              )}

              <GalleryAssetShortInfo clip={clip} />

              <div className="absolute inset-1 flex flex-col justify-between overflow-hidden rounded-[var(--gallery-card-inner-radius,12px)]">
                <div className="flex items-center justify-between p-2">
                  <div className="flex min-h-6 items-center gap-1">
                    {hasCachedPreview && ClipUtils.isProcessing(clip.status) && !clip.assets.spriteSheet?.src && (
                      <div className="flex h-6 items-center gap-1 rounded-4 bg-black/70 px-1 backdrop-blur-sm">
                        <Spinner className="size-3.5 text-white" />
                        <span className="font-semibold text-12 text-white">Processing…</span>
                      </div>
                    )}
                    {startBadges}
                    {!!popoverCustomFields?.length && canViewCustomFields && (
                      <GalleryCardCustomFieldsAction customFields={popoverCustomFields} onClick={onCustomFieldsClick} />
                    )}
                    {showVersionsIndicator && !!onVersionsClick && (
                      <CardAction
                        data-testid="CARD_VERSIONS_INDICATOR"
                        icon={<LayersIcon size="sm" />}
                        onClick={onVersionsClick}
                      />
                    )}
                    {shouldShowDiscussions && !!onDiscussionsClick && !!clip.openCommentCount && (
                      <CardAction
                        data-testid="CARD_DISCUSSIONS_INDICATOR"
                        onClick={onDiscussionsClick}
                        icon={<MessageSquareIcon size="sm" />}
                        text={clip.openCommentCount}
                      />
                    )}
                  </div>
                  <div className="pointer-events-auto flex items-center gap-1">
                    {isHovering && onOpenInCanvas && (
                      <Tooltip label="Edit in Canvas" side="top" sideOffset={4}>
                        <CardAction
                          aria-label="Edit in Canvas"
                          data-testid="ASSET_CARD_OPEN_IN_CANVAS_BUTTON"
                          icon={<MousePointerClickIcon size="sm" />}
                          onClick={handleOpenInCanvas}
                        />
                      </Tooltip>
                    )}
                    {isHovering && (
                      <GalleryAssetMenu
                        clip={clip}
                        getItemMenuOptions={getItemMenuOptions}
                        getSelectionMenuOptions={getSelectionMenuOptions}
                        setIsMenuOpen={setIsMenuOpen}
                        trigger={
                          <CardAction data-testid="ASSET_CARD_SUB_MENU_BUTTON" icon={<EllipsisIcon size="sm" />} />
                        }
                      />
                    )}
                  </div>
                </div>
                {!isTitleAndMetadataVisible && (
                  <div className="flex min-h-24 flex-col items-stretch justify-end gap-0.5 bg-gradient-to-t from-black/85 to-transparent p-2 opacity-0 transition-opacity group-focus-within/gallery-selectable-card:opacity-100 group-data-[hovering=true]/gallery-selectable-card:opacity-100">
                    <GalleryAssetTitle
                      className="text-white"
                      clip={clip}
                      onClick={onTitleClick}
                      style={{
                        textShadow: '0px 0px 4px rgba(0, 0, 0, 0.40)',
                      }}
                    />
                    <p className="truncate text-12 text-white">{meta}</p>
                  </div>
                )}
              </div>
            </div>

            {(isTitleAndMetadataVisible || shouldShowCustomFields) && (
              <div className="pointer-events-none z-10 flex flex-col items-stretch gap-2 px-3 pt-1 pb-2">
                {isTitleAndMetadataVisible && (
                  <div className="flex flex-col items-stretch gap-0.5">
                    <GalleryAssetTitle clip={clip} onClick={onTitleClick} />
                    <p className="truncate ads:text-metadata text-12 text-gray-10">{meta}</p>
                  </div>
                )}
                {shouldShowCustomFields && (
                  <CustomFieldsDisplay customFields={customFields} onClick={onCustomFieldsClick} />
                )}
              </div>
            )}
          </>
        );
      }}
    </GallerySelectableCard>
  );
};

// biome-ignore lint/plugin: memo erases generic params; typeof cast restores inference
export const GalleryAsset = memo(_GalleryAsset) as typeof _GalleryAsset;
